/**
 * Loyalty System - Kundenbindung (Self-Service)
 * 
 * Der Kunde konfiguriert alles selbst:
 * - Stempelkarten-Regeln
 * - Punkte-System
 * - Belohnungen
 * - Geburtstags-Rabatte
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Gift, Users, Star, Cake, Settings, Save, RefreshCw,
  CreditCard, Award, TrendingUp, Search, Check, X,
  Percent, Euro, ShoppingBag
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function LoyaltySystem() {
  const navigate = useNavigate();
  const [sessionToken] = useState(() => localStorage.getItem('access_token'));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: false,
    points_per_euro: 1,
    points_for_signup: 50,
    stamps_enabled: true,
    stamps_per_order: 1,
    stamps_for_reward: 10,
    stamp_reward_type: 'discount_percent',
    stamp_reward_value: 10,
    stamp_reward_product: '',
    birthday_enabled: true,
    birthday_discount_percent: 15,
    birthday_valid_days: 7,
    points_per_euro_discount: 100,
    min_points_redeem: 100,
    max_discount_percent: 50
  });
  
  // Stats & Customers
  const [stats, setStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!sessionToken) {
      navigate('/login');
      return;
    }
    loadData();
  }, [sessionToken]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      const [settingsRes, statsRes, customersRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/loyalty/settings`, { headers }),
        fetch(`${BACKEND_URL}/api/loyalty/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/loyalty/customers?limit=50`, { headers })
      ]);
      
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        if (data.settings) {
          setSettings(prev => ({ ...prev, ...data.settings }));
        }
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      
      if (customersRes.ok) {
        const data = await customersRes.json();
        setCustomers(data.customers || []);
      }
      
    } catch (error) {
      console.error('Load error:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/loyalty/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionToken}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Einstellungen gespeichert!');
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const searchCustomers = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/loyalty/customers?search=${encodeURIComponent(searchQuery)}`,
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers || []);
      }
    } catch (error) {
      console.error('Search error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Header */}
      <header className="bg-[#111C32] border-b border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Gift className="w-6 h-6 text-amber-400" />
              Kundenbindung
            </h1>
            <p className="text-sm text-slate-400">
              Stempelkarten, Punkte & Belohnungen - alles selbst einrichten
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <Badge 
              variant={settings.enabled ? "default" : "secondary"}
              className={settings.enabled ? "bg-emerald-500" : "bg-slate-600"}
            >
              {settings.enabled ? "Aktiv" : "Deaktiviert"}
            </Badge>
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Speichern
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="settings" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="settings">
              <Settings className="w-4 h-4 mr-2" />
              Einstellungen
            </TabsTrigger>
            <TabsTrigger value="customers">
              <Users className="w-4 h-4 mr-2" />
              Kunden ({stats?.total_customers || 0})
            </TabsTrigger>
            <TabsTrigger value="stats">
              <TrendingUp className="w-4 h-4 mr-2" />
              Statistiken
            </TabsTrigger>
          </TabsList>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-6">
            
            {/* Master Switch */}
            <Card className="bg-[#111C32] border-slate-700">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-white">Loyalty-System aktivieren</h3>
                    <p className="text-sm text-slate-400">
                      Wenn aktiviert, können Kunden Punkte sammeln und Belohnungen erhalten
                    </p>
                  </div>
                  <Switch
                    checked={settings.enabled}
                    onCheckedChange={(checked) => setSettings({...settings, enabled: checked})}
                  />
                </div>
              </CardContent>
            </Card>

            {settings.enabled && (
              <>
                {/* Stempelkarte */}
                <Card className="bg-[#111C32] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-blue-400" />
                      Digitale Stempelkarte
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Kunden sammeln Stempel bei jeder Bestellung
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-300">Stempelkarte aktivieren</Label>
                      <Switch
                        checked={settings.stamps_enabled}
                        onCheckedChange={(checked) => setSettings({...settings, stamps_enabled: checked})}
                      />
                    </div>
                    
                    {settings.stamps_enabled && (
                      <>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-slate-300">Stempel pro Bestellung</Label>
                            <Input
                              type="number"
                              value={settings.stamps_per_order}
                              onChange={(e) => setSettings({...settings, stamps_per_order: parseInt(e.target.value) || 1})}
                              min={1}
                              max={5}
                              className="bg-slate-800 border-slate-600 text-white mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-slate-300">Stempel für Belohnung</Label>
                            <Input
                              type="number"
                              value={settings.stamps_for_reward}
                              onChange={(e) => setSettings({...settings, stamps_for_reward: parseInt(e.target.value) || 10})}
                              min={5}
                              max={20}
                              className="bg-slate-800 border-slate-600 text-white mt-1"
                            />
                          </div>
                        </div>
                        
                        <div className="p-4 bg-slate-800/50 rounded-lg">
                          <Label className="text-slate-300 mb-3 block">Belohnung bei voller Karte</Label>
                          <Select
                            value={settings.stamp_reward_type}
                            onValueChange={(value) => setSettings({...settings, stamp_reward_type: value})}
                          >
                            <SelectTrigger className="bg-slate-800 border-slate-600 mb-3">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                              <SelectItem value="discount_percent">Prozent-Rabatt</SelectItem>
                              <SelectItem value="discount_fixed">Fester Euro-Rabatt</SelectItem>
                              <SelectItem value="free_product">Gratis-Produkt</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {settings.stamp_reward_type === 'discount_percent' && (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={settings.stamp_reward_value}
                                onChange={(e) => setSettings({...settings, stamp_reward_value: parseFloat(e.target.value) || 10})}
                                className="bg-slate-800 border-slate-600 text-white w-24"
                              />
                              <span className="text-slate-400">% Rabatt auf die Bestellung</span>
                            </div>
                          )}
                          
                          {settings.stamp_reward_type === 'discount_fixed' && (
                            <div className="flex items-center gap-2">
                              <Input
                                type="number"
                                value={settings.stamp_reward_value}
                                onChange={(e) => setSettings({...settings, stamp_reward_value: parseFloat(e.target.value) || 5})}
                                className="bg-slate-800 border-slate-600 text-white w-24"
                              />
                              <span className="text-slate-400">€ Rabatt</span>
                            </div>
                          )}
                          
                          {settings.stamp_reward_type === 'free_product' && (
                            <Input
                              value={settings.stamp_reward_product}
                              onChange={(e) => setSettings({...settings, stamp_reward_product: e.target.value})}
                              placeholder="z.B. Kaffee, Dessert, ..."
                              className="bg-slate-800 border-slate-600 text-white"
                            />
                          )}
                        </div>
                        
                        <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                          <p className="text-sm text-blue-300">
                            📱 Beispiel: Nach {settings.stamps_for_reward} Bestellungen erhält der Kunde{' '}
                            {settings.stamp_reward_type === 'discount_percent' && `${settings.stamp_reward_value}% Rabatt`}
                            {settings.stamp_reward_type === 'discount_fixed' && `${settings.stamp_reward_value}€ Rabatt`}
                            {settings.stamp_reward_type === 'free_product' && `"${settings.stamp_reward_product || 'Gratis-Produkt'}" gratis`}
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Punkte-System */}
                <Card className="bg-[#111C32] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Star className="w-5 h-5 text-amber-400" />
                      Punkte-System
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Kunden sammeln Punkte und lösen sie für Rabatte ein
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300">Punkte pro Euro Umsatz</Label>
                        <Input
                          type="number"
                          step="0.1"
                          value={settings.points_per_euro}
                          onChange={(e) => setSettings({...settings, points_per_euro: parseFloat(e.target.value) || 1})}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Willkommens-Punkte</Label>
                        <Input
                          type="number"
                          value={settings.points_for_signup}
                          onChange={(e) => setSettings({...settings, points_for_signup: parseInt(e.target.value) || 0})}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-slate-300">Punkte für 1€ Rabatt</Label>
                        <Input
                          type="number"
                          value={settings.points_per_euro_discount}
                          onChange={(e) => setSettings({...settings, points_per_euro_discount: parseInt(e.target.value) || 100})}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Mindestpunkte einlösen</Label>
                        <Input
                          type="number"
                          value={settings.min_points_redeem}
                          onChange={(e) => setSettings({...settings, min_points_redeem: parseInt(e.target.value) || 100})}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300">Max. Rabatt %</Label>
                        <Input
                          type="number"
                          value={settings.max_discount_percent}
                          onChange={(e) => setSettings({...settings, max_discount_percent: parseFloat(e.target.value) || 50})}
                          className="bg-slate-800 border-slate-600 text-white mt-1"
                        />
                      </div>
                    </div>
                    
                    <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                      <p className="text-sm text-amber-300">
                        💡 Beispiel: Bei einer 20€ Bestellung erhält der Kunde {20 * settings.points_per_euro} Punkte.
                        Mit {settings.points_per_euro_discount} Punkten kann er 1€ sparen.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Geburtstag */}
                <Card className="bg-[#111C32] border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Cake className="w-5 h-5 text-pink-400" />
                      Geburtstags-Rabatt
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Automatischer Rabatt zum Geburtstag des Kunden
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-slate-300">Geburtstags-Rabatt aktivieren</Label>
                      <Switch
                        checked={settings.birthday_enabled}
                        onCheckedChange={(checked) => setSettings({...settings, birthday_enabled: checked})}
                      />
                    </div>
                    
                    {settings.birthday_enabled && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-slate-300">Rabatt in %</Label>
                          <Input
                            type="number"
                            value={settings.birthday_discount_percent}
                            onChange={(e) => setSettings({...settings, birthday_discount_percent: parseFloat(e.target.value) || 15})}
                            className="bg-slate-800 border-slate-600 text-white mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-slate-300">Gültig für X Tage</Label>
                          <Input
                            type="number"
                            value={settings.birthday_valid_days}
                            onChange={(e) => setSettings({...settings, birthday_valid_days: parseInt(e.target.value) || 7})}
                            className="bg-slate-800 border-slate-600 text-white mt-1"
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* CUSTOMERS TAB */}
          <TabsContent value="customers" className="space-y-4">
            <Card className="bg-[#111C32] border-slate-700">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Loyalty-Kunden</CardTitle>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Name, Telefon oder E-Mail..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-64 bg-slate-800 border-slate-600 text-white"
                      onKeyPress={(e) => e.key === 'Enter' && searchCustomers()}
                    />
                    <Button variant="outline" onClick={searchCustomers} className="border-slate-600">
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {customers.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Name</TableHead>
                        <TableHead className="text-slate-400">Kontakt</TableHead>
                        <TableHead className="text-slate-400 text-right">Punkte</TableHead>
                        <TableHead className="text-slate-400 text-right">Stempel</TableHead>
                        <TableHead className="text-slate-400 text-right">Umsatz</TableHead>
                        <TableHead className="text-slate-400">Letzter Besuch</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {customers.map((customer) => (
                        <TableRow key={customer.customer_id} className="border-slate-700">
                          <TableCell className="text-white font-medium">
                            {customer.name}
                            {customer.birthday && (
                              <Cake className="w-3 h-3 inline ml-2 text-pink-400" />
                            )}
                          </TableCell>
                          <TableCell className="text-slate-400">
                            {customer.phone || customer.email || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary" className="bg-amber-500/20 text-amber-400">
                              {customer.points_balance} P
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="secondary" className="bg-blue-500/20 text-blue-400">
                              {customer.stamps_balance}/{settings.stamps_for_reward}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-emerald-400">
                            €{customer.total_spent?.toFixed(2) || '0.00'}
                          </TableCell>
                          <TableCell className="text-slate-400 text-sm">
                            {customer.last_visit 
                              ? new Date(customer.last_visit).toLocaleDateString('de-DE')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Noch keine Loyalty-Kunden</p>
                    <p className="text-sm mt-1">Kunden werden automatisch hinzugefügt wenn sie bestellen</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATS TAB */}
          <TabsContent value="stats" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Kunden gesamt</p>
                      <p className="text-2xl font-bold text-white">{stats?.total_customers || 0}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Aktive Kunden (30 Tage)</p>
                      <p className="text-2xl font-bold text-white">{stats?.active_customers || 0}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Punkte im Umlauf</p>
                      <p className="text-2xl font-bold text-white">{stats?.total_points_in_circulation || 0}</p>
                    </div>
                    <Star className="w-8 h-8 text-amber-400" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#111C32] border-slate-700">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Ø Kundenwert</p>
                      <p className="text-2xl font-bold text-white">€{stats?.average_customer_value?.toFixed(2) || '0.00'}</p>
                    </div>
                    <Euro className="w-8 h-8 text-emerald-400" />
                  </div>
                </CardContent>
              </Card>
            </div>
            
            <Card className="bg-[#111C32] border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Einlösungen (letzte 30 Tage)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Award className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                  <p className="text-3xl font-bold text-white">{stats?.redemptions_30_days || 0}</p>
                  <p className="text-slate-400">Belohnungen eingelöst</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
